import type { Router } from 'vue-router'
import type { TabsModuleType } from '@/types/store'

function readMicroSource(source?: { getData?: () => any }) {
  try {
    return source?.getData?.() || {}
  }
  catch {
    return {}
  }
}

function assignDefined(target: Record<string, any>, source: Record<string, any>) {
  Object.entries(source || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null)
      target[key] = value
  })
  return target
}

function assignIfAbsent(target: Record<string, any>, source: Record<string, any>) {
  Object.entries(source || {}).forEach(([key, value]) => {
    if (value === undefined || value === null)
      return
    if (target[key] === undefined || target[key] === null)
      target[key] = value
  })
  return target
}

/** 禁止访问 window.parent，iframe 沙箱里会 RangeError: Maximum call stack size exceeded */
export function getEventCenter() {
  if (!window?.__MICRO_APP_ENVIRONMENT__)
    return undefined

  const key = `eventCenterFor${window.__MICRO_APP_NAME__}`
  try {
    if (window.rawWindow?.[key])
      return window.rawWindow[key]
  }
  catch {}
  try {
    if ((window as any)[key])
      return (window as any)[key]
  }
  catch {}
  return undefined
}

/**
 * 与基座进行数据交互 - 获取
 * 一层：rawWindow EventCenter
 * 二层：EventCenter 没有的字段用 window.microApp.getData 补
 */
export function getMicroData(): {
  systemId: number
  mainAppRouter: Router
  tabsStore: TabsModuleType
  setTitle?: (title: any) => void
  delVisitedRoute: (str: string) => void
  isLoadCommonCss: boolean
  [key: string]: any
} {
  const result: Record<string, any> = { isLoadCommonCss: true }
  assignDefined(result, readMicroSource(getEventCenter()))
  assignIfAbsent(result, readMicroSource((window as any).microApp))
  return result as any
}

/**
 * 与基座进行数据交互 - 发送
 */
export function sendMicroData(data: any) {
  // 深层嵌套必须先走 window.microApp.dispatch，才能触发直接父应用 <micro-app @datachange>
  try {
    if ((window as any).microApp?.dispatch) {
      ;(window as any).microApp.dispatch(data)
      return
    }
  }
  catch {}
  try {
    getEventCenter()?.dispatch?.(data)
  }
  catch {}
}

function dataToken(data: any) {
  if (!data || typeof data !== 'object')
    return String(data)
  return [data.type, data.path, data.openKey, data.systemId, data.isMicroAppDone].join('|')
}

/** 同时监听 EventCenter / microApp，相同业务 token 只处理一次 */
export function bindMicroDataListener(listener: (data: any) => void, autoTrigger = false) {
  let last = ''
  const wrapped = (data: any) => {
    const raw = dataToken(data)
    if (raw === last)
      return
    last = raw
    listener(data)
  }

  try {
    getEventCenter()?.addDataListener?.(wrapped, autoTrigger)
  }
  catch {}
  try {
    const microApp = (window as any).microApp
    if (microApp?.addDataListener)
      microApp.addDataListener(wrapped, autoTrigger)
  }
  catch {}
  return wrapped
}
